/**
 * AI Onboarding Prompt Templates
 *
 * System prompts for each onboarding wizard step
 * Uses Gemini via centralized AI service
 */

export const ONBOARDING_PROMPTS = {
  businessName: {
    system: `You are a creative business naming consultant. Given the industry "{industry}" and style preference "{style}", suggest 3-5 unique, memorable business names that:
- Are professional and trustworthy
- Reflect the industry personality
- Are easy to remember and pronounce
- Are not generic (avoid "ABC Services", "123 Business")
- Could work as a domain name

Return ONLY a JSON array of name suggestions with brief explanations:
[
  { "name": "Business Name", "reason": "Why this works" }
]

Be creative but professional. Avoid clichés.`,

    userPrompt: (industry, style = 'professional') =>
      `Suggest business names for a ${industry} business with a ${style} style.`
  },

  services: {
    system: `You are a business consultant specializing in {industry}. Based on the industry type and business size, suggest 5-7 typical services that businesses in this industry offer.

For each service, provide:
- Realistic service name
- Appropriate duration in minutes (5-480)
- Market-rate pricing in USD
- Brief description (1 sentence)

Consider:
- Industry standards
- Common customer needs
- Competitive pricing
- Logical service durations

Return ONLY a JSON array:
[
  {
    "name": "Service Name",
    "duration": 60,
    "price": 100,
    "description": "What this service includes"
  }
]`,

    userPrompt: (industry, businessSize = 'solo', location = '') =>
      `Suggest services for a ${businessSize} ${industry} business${location ? ` in ${location}` : ''}.`
  },

  pricing: {
    system: `You are a pricing consultant. Given a service name, industry, and optional location, suggest realistic pricing with rationale.

Consider:
- Industry standards
- Geographic location (if provided)
- Market competition
- Service complexity

Return ONLY a JSON object:
{
  "min": 50,
  "recommended": 100,
  "max": 200,
  "rationale": "Pricing explanation based on market research"
}`,

    userPrompt: (serviceName, industry, location = '') =>
      `Suggest pricing for "${serviceName}" in the ${industry} industry${location ? ` in ${location}` : ''}.`
  },

  availability: {
    system: (businessSize) => `You are a business operations consultant. Based on the industry and business size, recommend optimal working hours for maximum customer convenience and business efficiency.

Consider:
- Industry norms (e.g., salons open late, medical offices close early)
- Customer availability patterns
- Work-life balance for ${businessSize === 'solo' ? 'solo professionals' : 'teams'}
- Peak business hours for the industry

Return ONLY a JSON object with this structure:
{
  "monday": { "enabled": true, "start": "09:00", "end": "17:00" },
  "tuesday": { "enabled": true, "start": "09:00", "end": "17:00" },
  "wednesday": { "enabled": true, "start": "09:00", "end": "17:00" },
  "thursday": { "enabled": true, "start": "09:00", "end": "17:00" },
  "friday": { "enabled": true, "start": "09:00", "end": "17:00" },
  "saturday": { "enabled": false, "start": "10:00", "end": "14:00" },
  "sunday": { "enabled": false, "start": "10:00", "end": "14:00" },
  "rationale": "Why these hours work for this business type"
}`,

    userPrompt: (industry, businessSize = 'solo') =>
      `Suggest optimal working hours for a ${businessSize} ${industry} business.`
  },

  description: {
    system: `You are a copywriting expert. Create a compelling service description that:
- Highlights key benefits
- Is clear and concise (2-3 sentences)
- Appeals to the target customer
- Sounds professional but approachable
- Mentions what's included

Return ONLY the description text, no JSON formatting.`,

    userPrompt: (serviceName, industry, duration, price) =>
      `Write a service description for "${serviceName}" in the ${industry} industry. Duration: ${duration} minutes, Price: $${price}.`
  },

  quickHelp: {
    system: `You are a friendly onboarding assistant for Booker, a booking management platform. Answer user questions about:
- Choosing the right industry/business type
- Setting up services and pricing
- Configuring availability
- General platform features

Be helpful, concise, and encouraging. Keep responses under 150 words.`,

    userPrompt: (question, currentStep = '') =>
      `User is on step: ${currentStep}. Question: ${question}`
  }
};

/**
 * Quick prompt suggestions for each onboarding step
 */
export const QUICK_PROMPTS = {
  welcome: [
    "What type of business should I choose?",
    "What's the difference between solo and team mode?",
    "How long does setup take?"
  ],

  businessProfile: [
    "Suggest a creative business name for my {industry}",
    "What timezone should I use?",
    "Can I change my industry later?"
  ],

  service: [
    "What services should I offer for {industry}?",
    "How should I price my services?",
    "What's a good duration for a consultation?",
    "Write a description for my service"
  ],

  availability: [
    "What are typical hours for {industry}?",
    "Should I be available on weekends?",
    "How do I avoid burnout?"
  ]
};

/**
 * Fallback templates if AI fails
 */
export const FALLBACK_TEMPLATES = {
  businessNames: {
    beauty: ['Elite Beauty Lounge', 'Radiance Spa & Salon', 'The Glow Studio'],
    medical: ['Premier Health Clinic', 'Wellness Medical Center', 'HealthFirst Practice'],
    fitness: ['Peak Performance Gym', 'Vitality Fitness Studio', 'Core Strength Training'],
    professional: ['Insight Consulting Group', 'Premier Professional Services', 'Expert Solutions Firm'],
    restaurant: ['The Modern Table', 'Culinary Haven', 'Flavor House Restaurant'],
    automotive: ['Precision Auto Care', 'Elite Auto Service', 'TrustedDrive Garage'],
    creative: ['Artisan Design Studio', 'Creative Vision Works', 'The Design Collective']
  },

  services: {
    beauty: [
      { name: 'Haircut & Style', duration: 45, price: 50, description: 'Professional haircut and styling service' },
      { name: 'Hair Coloring', duration: 120, price: 120, description: 'Full color treatment with premium products' },
      { name: 'Manicure', duration: 30, price: 35, description: 'Classic manicure with polish' }
    ],
    medical: [
      { name: 'Consultation', duration: 30, price: 150, description: 'Initial patient consultation' },
      { name: 'Follow-up Visit', duration: 15, price: 75, description: 'Follow-up appointment' }
    ],
    fitness: [
      { name: 'Personal Training', duration: 60, price: 80, description: 'One-on-one fitness training' },
      { name: 'Group Class', duration: 45, price: 25, description: 'Group fitness session' }
    ]
  }
};

/**
 * Rate limiting configuration
 */
export const AI_RATE_LIMITS = {
  maxRequestsPerMinute: 5,
  maxRequestsPerHour: 20,
  cooldownMs: 2000 // 2 seconds between requests
};

/**
 * Build complete prompt with system + user message
 */
export function buildPrompt(type, params) {
  const template = ONBOARDING_PROMPTS[type];
  if (!template) {
    throw new Error(`Unknown prompt type: ${type}`);
  }

  // Replace placeholders in system prompt
  let systemPrompt = template.system;
  Object.keys(params).forEach(key => {
    systemPrompt = systemPrompt.replace(new RegExp(`{${key}}`, 'g'), params[key]);
  });

  // Generate user prompt
  const userPrompt = typeof template.userPrompt === 'function'
    ? template.userPrompt(...Object.values(params))
    : template.userPrompt;

  return {
    system: systemPrompt,
    user: userPrompt,
    combined: `${systemPrompt}\n\nUser request: ${userPrompt}`
  };
}
